import { shouldUseEngageDomain as shouldUseEngageDomainSelector } from '@ms/yammer-data/dist/domains/domainMigration/selectors';
import { loadEcsTreatments } from '@ms/yammer-data/dist/domains/ecs/actions';
import { addEtagToAnalyticsEvent } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsEvent';
import { addEtagToAnalyticsV2Event } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsV2Event';
import { load as loadFeatureAccessSettingsAction } from '@ms/yammer-data/dist/domains/featureAccessSettings/actions';
import { isFlightControlPanelEnabled as isFlightControlPanelEnabledSelector } from '@ms/yammer-data/dist/domains/flightControlPanel/selectors';
import { load as loadHomeNetworkRedirect } from '@ms/yammer-data/dist/domains/homeNetworkRedirect/actions';
import { setNetworkContext } from '@ms/yammer-data/dist/domains/networkContext/actions';
import { setPersistKey } from '@ms/yammer-data/dist/domains/persistConfig/actions';
import { getRawUserLocaleSetting } from '@ms/yammer-data/dist/domains/session/selectors';
import { configure } from '@ms/yammer-data/dist/domains/sessionConfigure/actions';
import { loadCurrentUser } from '@ms/yammer-data/dist/domains/sessionLoadCurrentUser/actions';
import { setTelemetryUserContext } from '@ms/yammer-data/dist/domains/telemetry/actions';
import { updateOptInPreference } from '@ms/yammer-data/dist/domains/userPreferences/actions';
import type { ThunkDispatch } from '@ms/yammer-data/dist/types';
import { setDiagnosticsLoggingEnabled } from '@ms/yammer-diagnostics-state';
import { WebClientPreference } from '@ms/yammer-graphql';
import { setLocale, setLocaleFromBrowser as setLocaleFromBrowserI18nReact } from '@ms/yammer-i18n-react';
import {
  isDogfoodPerfHUDPersistEnabled,
  loadLogEventInDogfoodPerfHUDStoreMiddleware,
} from '@ms/yammer-page-route-telemetry';
import type { TelemetryMiddleware } from '@ms/yammer-telemetry';
import { acquireTokenWithTelemetry, addMiddleware, addUrlRootPathToEventMiddleware } from '@ms/yammer-telemetry';
import { getClientContext } from '@ms/yammer-telemetry-store';
import { acquireToken, getIdpFromToken } from '@ms/yammer-web-authenticators';
import { removeOptInProgressCookie } from '@ms/yammer-web-support/dist/cookies';
import { setPersistedStateConfigKeys } from '@ms/yammer-web-support/dist/hosting';
import {
  basenameForWebApp,
  getFirstResource,
  getNetworkContext,
  getQueryParameter,
  isDomainRedirect,
  isLoadedWithEngageDomain,
  redirectToEngageDomain,
  trimLeadingAndTrailingSlash,
} from '@ms/yammer-web-support/dist/location';
import { isOptInUnderProgress } from '@ms/yammer-web-support/dist/optIn';
import { feedPageUrl, getBasenameWithNetworkContext } from '@ms/yammer-web-support/dist/urls';

import type { YammerWebState } from '../../../../state/types';
import config from '../../../config';
import { writeCookies } from '../../../cookies';
import { persistConfig } from '../../../persistedState';
import { reportErrorWithCode, reportIsDomainRedirect } from '../bootstrapTelemetry';

type ActionDispatcher = (dispatch: ThunkDispatch<YammerWebState>) => Promise<void>;

export const ensureTokensAcquired: () => Promise<void> = async () => {
  const yammerToken = await acquireTokenWithTelemetry({
    scopes: [config.adal.resources.yammer],
    initiator: 'bootstrap',
  });
  try {
    writeCookies(yammerToken);
  } catch (error) {
    reportErrorWithCode(error, 'WriteHostingCookiesFailed');
  }
};

type SetupFeatureFlighting = (dispatch: ThunkDispatch<YammerWebState>, getState: () => YammerWebState) => Promise<void>;
export const setupFeatureFlighting: SetupFeatureFlighting = async (dispatch) => {
  try {
    await dispatch(loadEcsTreatments({ persistKey: persistConfig.key, initiator: 'bootstrap' }));
  } catch (error) {
    reportErrorWithCode(error, 'InitializeFeatureFlightingFailed');
  }
};

export const tryUpdateOptInPreference: (
  dispatch: ThunkDispatch<YammerWebState>,
  state: YammerWebState
) => Promise<void> = async (dispatch, state) => {
  const doesUserPreferModernClient =
    state.session.user.userPreferences.webClientPreference === WebClientPreference.MODERN;

  if (doesUserPreferModernClient) {
    return;
  }

  const userId = state.session.user.id;
  const isUserForceOptedIn = state.session.user.isUserForceOptedIn;
  if (isOptInUnderProgress() || isUserForceOptedIn) {
    try {
      const optinOrigin = isUserForceOptedIn ? 'forced' : 'toggle';
      await dispatch(updateOptInPreference(optinOrigin, userId));
      removeOptInProgressCookie();
    } catch (error) {
      reportErrorWithCode(error, 'UpdateModernClientPreferenceFailed');
    }
  }
};

export const loadUserData: ActionDispatcher = async (dispatch) => {
  try {
    await dispatch(loadCurrentUser({ initiator: 'bootstrap' }));
    await dispatch(setTelemetryUserContext());
  } catch (error) {
    reportErrorWithCode(error, 'LoadCurrentUserFailed');
    throw error;
  }
};

type ViewerIdentityProvidedByHomeTenant = (yammerToken: string) => boolean;
const viewerIdentityProvidedByHomeTenant: ViewerIdentityProvidedByHomeTenant = (yammerToken) =>
  !getIdpFromToken(yammerToken);

export const performHomeRedirect: ActionDispatcher = async (dispatch) => {
  try {
    const yammerToken = await acquireToken({ scopes: [config.adal.resources.yammer] });
    if (viewerIdentityProvidedByHomeTenant(yammerToken)) {
      return;
    }
    const resourceId = getFirstResource(window.location.pathname);
    await dispatch(loadHomeNetworkRedirect(resourceId));
  } catch (error) {
    reportErrorWithCode(error, 'PerformHomeRedirectFailed');
    throw error;
  }
};

export const savePersistKey: (dispatch: ThunkDispatch<YammerWebState>) => void = (dispatch) => {
  setPersistedStateConfigKeys(persistConfig.key);
  dispatch(setPersistKey(persistConfig.key));
};

export const setLocaleFromUserData: (dispatch: ThunkDispatch<YammerWebState>, state: YammerWebState) => void = (
  dispatch,
  state
) => {
  const overrideLocale = getQueryParameter('locale') as string;
  const locale = overrideLocale ? overrideLocale : getRawUserLocaleSetting(state);
  dispatch(setLocale(locale));
};

type SetLocaleFromBrowser = (dispatch: ThunkDispatch<YammerWebState>, state: YammerWebState) => void;
export const setLocaleFromBrowser: SetLocaleFromBrowser = (dispatch, state) => {
  const overrideLocale = getQueryParameter('locale') as string;

  setLocaleFromBrowserI18nReact(dispatch, state, overrideLocale);
};

const isLoadedFromTeamLab = () => window.location.hostname.endsWith('labs.yammer.dev');
const isLoadedFromNonProd = () => window.location.hostname.endsWith('dev.yammer.com');

type TryRedirectToEngageDomain = (dispatch: ThunkDispatch<YammerWebState>, state: YammerWebState) => Promise<void>;
export const tryRedirectToEngageDomain: TryRedirectToEngageDomain = async (_dispatch, state) => {
  if (isLoadedFromTeamLab() || isLoadedFromNonProd()) {
    return;
  }

  const isFromDomainRedirect = isDomainRedirect();
  const isLoadingEngageDomain = isLoadedWithEngageDomain();
  const { isPWA: isPWAFromQueryParam } = getClientContext();

  if (isFromDomainRedirect) {
    reportIsDomainRedirect();
  }

  const shouldUseEngageDomain = shouldUseEngageDomainSelector(state);
  if (shouldUseEngageDomain && !isLoadingEngageDomain) {
    redirectToEngageDomain(!!isPWAFromQueryParam);
  }
};

export const configureSession: (dispatch: ThunkDispatch<YammerWebState>) => void = (dispatch) => {
  dispatch(configure(config));
};

export const configureNetworkContext: (dispatch: ThunkDispatch<YammerWebState>) => void = (dispatch) => {
  const networkContext = getNetworkContext(window.location.pathname);
  dispatch(setNetworkContext(networkContext));
};

type GetAddEtagMiddleware = (getState: () => YammerWebState) => TelemetryMiddleware;
const getAddEtagToAnalyticsEventMiddleware: GetAddEtagMiddleware = (getState) => (event) =>
  addEtagToAnalyticsEvent(event, getState);

type GetAddEtagV2Middleware = (getState: () => YammerWebState) => TelemetryMiddleware;
const getAddEtagToAnalyticsV2EventMiddleware: GetAddEtagV2Middleware = (getState) => (event) =>
  addEtagToAnalyticsV2Event(event, getState);

export const addTelemetryMiddleware: (getState: () => YammerWebState) => Promise<void> = async (getState) => {
  const etagMiddleware = getAddEtagToAnalyticsEventMiddleware(getState);
  const etagV2Middleware = getAddEtagToAnalyticsV2EventMiddleware(getState);
  addMiddleware(etagMiddleware);
  addMiddleware(etagV2Middleware);
  addMiddleware(addUrlRootPathToEventMiddleware);

  const isPerfHUDEnabled = await isDogfoodPerfHUDPersistEnabled(persistConfig.key);
  if (isPerfHUDEnabled) {
    const { logEventInDogfoodPerfHUDStoreMiddleware } = await loadLogEventInDogfoodPerfHUDStoreMiddleware();
    addMiddleware(logEventInDogfoodPerfHUDStoreMiddleware);
  }
};

type LoadFeatureAccessSettings = (dispatch: ThunkDispatch<YammerWebState>) => Promise<void>;
export const loadFeatureAccessSettings: LoadFeatureAccessSettings = (dispatch) =>
  dispatch(loadFeatureAccessSettingsAction({ initiator: 'bootstrap' }));

type SetupDiagnosticsDebugLogging = (getState: () => YammerWebState) => void;
export const setupDiagnosticsDebugLogging: SetupDiagnosticsDebugLogging = (getState) => {
  if (isFlightControlPanelEnabledSelector(getState())) {
    setDiagnosticsLoggingEnabled(true);
  }
};

export const replaceRootPathWithHomeFeedPath = (pathname: string) => {
  const trimmedPathname = trimLeadingAndTrailingSlash(pathname);
  const trimmedBaseName = trimLeadingAndTrailingSlash(basenameForWebApp);
  if (trimmedPathname === '' || trimmedPathname === trimmedBaseName) {
    const homeFeedPath = `${basenameForWebApp}${feedPageUrl}`;

    history.replaceState(null, '', homeFeedPath);

    return;
  }

  const mtoNetworkContext = getNetworkContext(pathname);
  const mtoBasename = getBasenameWithNetworkContext(basenameForWebApp, mtoNetworkContext);
  if (mtoBasename !== basenameForWebApp && trimmedPathname === trimLeadingAndTrailingSlash(mtoBasename)) {
    history.replaceState(null, '', `${mtoBasename}${feedPageUrl}`);
  }
};
